// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ — use those instead.
import { useState, useEffect, useCallback } from "react";
import { getApiUrl, setApiUrl, fetchAutoCosMode, setAutoCosMode } from "../api/client.js";
import { useTheme, THEMES, type Theme } from "../hooks/useTheme.js";

type PushState = "loading" | "unsupported" | "denied" | "subscribed" | "unsubscribed";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function usePushSubscription() {
  const [state, setState] = useState<PushState>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkState = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return;
    }
    const perm = Notification.permission;
    if (perm === "denied") {
      setState("denied");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "subscribed" : "unsubscribed");
    } catch {
      setState("unsubscribed");
    }
  }, []);

  useEffect(() => { checkState(); }, [checkState]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState("denied");
        return;
      }
      const base = getApiUrl();
      const headers: Record<string, string> = { "Content-Type": "application/json" };

      const vapidRes = await fetch(`${base}/api/pwa/push/vapid-public-key`, {
        headers,
        credentials: "include",
      });
      if (!vapidRes.ok) throw new Error("Failed to fetch VAPID key");
      const { publicKey } = await vapidRes.json() as { publicKey: string };

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as Uint8Array<ArrayBuffer>,
      });

      const subJson = sub.toJSON();
      const subRes = await fetch(`${base}/api/pwa/push/subscribe`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          user_agent: navigator.userAgent,
        }),
      });
      if (!subRes.ok) throw new Error("Failed to register subscription on server");
      setState("subscribed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Subscribe failed");
    } finally {
      setBusy(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const base = getApiUrl();
        const headers: Record<string, string> = { "Content-Type": "application/json" };

        await fetch(`${base}/api/pwa/push/unsubscribe`, {
          method: "DELETE",
          headers,
          credentials: "include",
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setState("unsubscribed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unsubscribe failed");
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, error, subscribe, unsubscribe };
}

type AuthState = "checking" | "connected" | "not_connected";

function useAuthStatus() {
  const [authState, setAuthState] = useState<AuthState>("checking");

  const checkAuth = useCallback(async () => {
    const base = getApiUrl();
    if (!base) {
      setAuthState("not_connected");
      return;
    }
    try {
      const res = await fetch(`${base}/api/pwa/health`, {
        credentials: "include",
      });
      setAuthState(res.ok ? "connected" : "not_connected");
    } catch {
      setAuthState("not_connected");
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  return { authState, recheckAuth: checkAuth };
}

type AutoCosMode = "off" | "dry_run" | "on" | "paused";

function useAutoCosMode() {
  const [mode, setModeState] = useState<AutoCosMode>("off");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAutoCosMode()
      .then((r) => { setModeState(r.mode as AutoCosMode); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setLoading(false); });
  }, []);

  const setMode = useCallback(async (newMode: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await setAutoCosMode(newMode);
      setModeState(r.mode as AutoCosMode);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { mode, setMode, loading, error };
}

export function SettingsScreen({
  // Hidden when embedded inside the v4 shell (PWA V2 slice 4) — no point
  // advertising v4 from within v4.
  showV4Link = true,
}: { showV4Link?: boolean } = {}) {
  const [urlValue, setUrlValue] = useState(getApiUrl);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { theme, setTheme, resetTheme } = useTheme();
  const { authState, recheckAuth } = useAuthStatus();

  const {
    state: pushState,
    busy: pushBusy,
    error: pushError,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
  } = usePushSubscription();

  const {
    mode: autoCosMode,
    setMode: setAutoCosMode,
    loading: autoCosLoading,
    error: autoCosError,
  } = useAutoCosMode();

  const currentUrl = getApiUrl();
  const dirty = urlValue.trim() !== currentUrl;

  function handleSave() {
    if (!urlValue.trim()) {
      setError("API URL is required.");
      return;
    }
    setError(null);
    setApiUrl(urlValue.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    // Re-check auth after URL change
    setTimeout(() => recheckAuth(), 500);
  }

  function handleSignIn() {
    const base = getApiUrl();
    if (!base) return;
    window.location.href = `${base}/oauth2/start?rd=${encodeURIComponent(window.location.href)}`;
  }

  return (
    <>
      {/* Page header */}
      <div
        style={{
          padding: "18px 20px 12px",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--accent)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          Settings
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.025em",
            color: "var(--text)",
          }}
        >
          Connection
        </h1>
      </div>

      {/* Alkio Mobile v4 — PWA V2 mobile experience (slice 1) */}
      {showV4Link && (
      <button
        onClick={() => {
          window.location.hash = "v4";
        }}
        className="card"
        style={{
          display: "block",
          width: "calc(100% - 40px)",
          margin: "12px 20px 0",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>◆</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
              Alkio Mobile v4
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              New mobile experience · drill-through + Activity heroes
            </div>
          </div>
          <span style={{ color: "var(--accent)", fontSize: 16 }}>›</span>
        </div>
      </button>
      )}

      {/* Success banner */}
      {saved && (
        <div
          style={{
            background: "var(--ok-soft)",
            borderBottom: "1px solid var(--ok)",
            padding: "10px 20px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ok)",
            letterSpacing: "0.06em",
          }}
        >
          Saved successfully
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            background: "var(--danger-soft)",
            borderBottom: "1px solid var(--danger)",
            padding: "10px 20px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--danger)",
            letterSpacing: "0.06em",
          }}
        >
          {error}
        </div>
      )}

      {/* Current values (read-only display) */}
      <div
        style={{
          margin: "18px 20px 0",
          background: "var(--bg-elev)",
          borderRadius: 12,
          border: "1px solid var(--border-soft)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 16px" }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              marginBottom: 4,
            }}
          >
            Current API URL
          </div>
          <div
            style={{
              fontSize: 13,
              color: currentUrl ? "var(--text)" : "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              wordBreak: "break-all",
            }}
          >
            {currentUrl || "Not set"}
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div
        style={{
          margin: "20px 20px 0",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "var(--font-mono)",
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 14,
          }}
        >
          Update Settings
        </div>

        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-muted)",
            marginBottom: 6,
          }}
        >
          API URL
          <input
            type="url"
            value={urlValue}
            onChange={(e) => {
              setUrlValue(e.target.value);
              setSaved(false);
            }}
            placeholder="https://api.example.com"
            style={{
              display: "block",
              width: "100%",
              background: "var(--bg-elev)",
              color: "var(--text)",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 14,
              border: "1px solid var(--border)",
              outline: "none",
              marginTop: 6,
              marginBottom: 14,
              fontFamily: "var(--font-mono)",
              boxSizing: "border-box",
            }}
          />
        </label>

        <button
          onClick={handleSave}
          disabled={!urlValue.trim() || !dirty}
          style={{
            display: "block",
            width: "100%",
            background: dirty && urlValue.trim() ? "var(--accent)" : "var(--bg-elev-2)",
            color: dirty && urlValue.trim() ? "var(--bg)" : "var(--text-dim)",
            fontWeight: 600,
            borderRadius: 12,
            padding: "13px 0",
            fontSize: 15,
            marginTop: 8,
            border: "none",
            cursor: dirty && urlValue.trim() ? "pointer" : "not-allowed",
            opacity: !urlValue.trim() ? 0.4 : 1,
            transition: "background 0.15s, color 0.15s",
            fontFamily: "inherit",
          }}
        >
          Save
        </button>
      </div>

      {/* Google Account */}
      {currentUrl && (
        <div style={{ margin: "28px 20px 0" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              marginBottom: 14,
            }}
          >
            Google Account
          </div>

          <div
            style={{
              background: "var(--bg-elev)",
              borderRadius: 12,
              border: "1px solid var(--border-soft)",
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>
                  {authState === "checking"
                    ? "Checking..."
                    : authState === "connected"
                      ? "Connected"
                      : "Not signed in"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {authState === "connected"
                    ? "Authenticated via Google OAuth"
                    : "Sign in to access Alkio data"}
                </div>
              </div>

              <button
                onClick={handleSignIn}
                style={{
                  flexShrink: 0,
                  background: authState === "connected" ? "var(--bg-elev-2)" : "var(--accent)",
                  color: authState === "connected" ? "var(--text-muted)" : "var(--bg)",
                  border: authState === "connected" ? "1px solid var(--border)" : "none",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "var(--font-mono)",
                  cursor: "pointer",
                  letterSpacing: "0.04em",
                }}
              >
                {authState === "connected" ? "Re-sign in" : "Sign in"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push notifications */}
      {pushState !== "unsupported" && (
        <div style={{ margin: "28px 20px 0" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              marginBottom: 14,
            }}
          >
            Push Notifications
          </div>

          <div
            style={{
              background: "var(--bg-elev)",
              borderRadius: 12,
              border: "1px solid var(--border-soft)",
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>
                  {pushState === "subscribed" ? "Notifications enabled" : "Notifications disabled"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {pushState === "denied"
                    ? "Permission blocked \u2014 enable in browser settings"
                    : pushState === "subscribed"
                      ? "You will receive push notifications"
                      : pushState === "loading"
                        ? "Checking status..."
                        : "Enable to receive alerts when you\u2019re away"}
                </div>
              </div>

              {pushState !== "denied" && pushState !== "loading" && (
                <button
                  onClick={pushState === "subscribed" ? pushUnsubscribe : pushSubscribe}
                  disabled={pushBusy}
                  style={{
                    flexShrink: 0,
                    background: pushState === "subscribed" ? "var(--bg-elev-2)" : "var(--accent)",
                    color: pushState === "subscribed" ? "var(--text-muted)" : "var(--bg)",
                    border: pushState === "subscribed" ? "1px solid var(--border)" : "none",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "var(--font-mono)",
                    cursor: pushBusy ? "not-allowed" : "pointer",
                    opacity: pushBusy ? 0.5 : 1,
                    letterSpacing: "0.04em",
                  }}
                >
                  {pushBusy
                    ? "..."
                    : pushState === "subscribed"
                      ? "Disable"
                      : "Enable"}
                </button>
              )}
            </div>

            {pushError && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: "var(--danger-soft)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--danger)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {pushError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Theme */}
      <div style={{ margin: "28px 20px 0" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--accent)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Theme
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            overflowX: "auto",
          }}
        >
          {THEMES.map((t) => {
            const isActive = theme === t.id;
            const swatchColors: Record<Theme, { bg: string; accent: string }> = {
              default: { bg: "#161A21", accent: "#7FB6E8" },
              cocoa: { bg: "#1E1814", accent: "#E0A86A" },
              paper: { bg: "#F5F3EE", accent: "#4A56C8" },
              sage: { bg: "#EFEDE2", accent: "#B95A3F" },
            };
            const colors = swatchColors[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                style={{
                  flex: "1 0 0",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 4px",
                  background: "var(--bg-elev)",
                  border: isActive ? "2px solid var(--accent)" : "1px solid var(--border-soft)",
                  borderRadius: 12,
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: colors.bg,
                    border: `2px solid ${colors.accent}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: colors.accent,
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "var(--font-mono)",
                    color: isActive ? "var(--accent)" : "var(--text-muted)",
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {t.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Automation */}
      <div style={{ margin: "28px 20px 0" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--accent)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Automation
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          Auto-COS mode
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(["off", "dry_run", "on", "paused"] as AutoCosMode[]).map((m) => (
            <button
              key={m}
              disabled={autoCosLoading}
              onClick={() => { void setAutoCosMode(m); }}
              style={{
                flex: "1 0 0",
                padding: "8px 4px",
                background: autoCosMode === m
                  ? (m === "paused" ? "#d97706" : "var(--accent)")
                  : "var(--bg-elev)",
                color: autoCosMode === m ? "var(--bg)" : "var(--text)",
                border: autoCosMode === m
                  ? (m === "paused" ? "2px solid #d97706" : "2px solid var(--accent)")
                  : "1px solid var(--border-soft)",
                borderRadius: 8,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: autoCosMode === m ? 700 : 400,
                cursor: autoCosLoading ? "not-allowed" : "pointer",
                opacity: autoCosLoading ? 0.6 : 1,
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              {m === "dry_run" ? "DRY RUN" : m.toUpperCase()}
            </button>
          ))}
        </div>
        {autoCosError && (
          <div
            style={{
              marginTop: 8,
              padding: "6px 10px",
              background: "var(--danger-soft)",
              borderRadius: 8,
              fontSize: 12,
              color: "var(--danger)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {autoCosError}
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div
        style={{
          margin: "32px 20px 0",
          paddingBottom: 80,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "var(--font-mono)",
            color: "var(--text-dim)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 12,
          }}
        >
          Danger Zone
        </div>
        <div
          style={{
            background: "var(--danger-soft)",
            border: "1px solid var(--danger)",
            borderRadius: 12,
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
                marginBottom: 2,
              }}
            >
              Reset all settings
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Clears all settings and resets theme to default
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem("alkio_api_url");
              setUrlValue("");
              setSaved(false);
              resetTheme();
            }}
            style={{
              flexShrink: 0,
              background: "var(--danger-soft)",
              border: "1px solid var(--danger)",
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              color: "var(--danger)",
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            Reset
          </button>
        </div>
      </div>
    </>
  );
}
